import { create } from "zustand";
import { api } from "@/lib/api";

interface Message {
  id: string;
  projectId: string;
  role: "user" | "assistant" | "system";
  content: string;
  metadata?: any;
  createdAt: string;
}

interface ChatState {
  messages: Message[];
  streaming: boolean;
  streamingContent: string;
  error: string | null;
  loadHistory: (projectId: string) => Promise<void>;
  sendMessage: (projectId: string, content: string) => Promise<void>;
  clearMessages: () => void;
}

export const useChatStore = create<ChatState>((set) => ({
  messages: [],
  streaming: false,
  streamingContent: "",
  error: null,

  loadHistory: async (projectId) => {
    try {
      const messages = await api.getChatHistory(projectId);
      set({ messages, error: null });
    } catch (err: any) {
      set({ error: err.message });
    }
  },

  sendMessage: async (projectId, content) => {
    // Add user message optimistically
    const userMsg: Message = {
      id: crypto.randomUUID(),
      projectId,
      role: "user",
      content,
      createdAt: new Date().toISOString(),
    };

    set((s) => ({
      messages: [...s.messages, userMsg],
      streaming: true,
      streamingContent: "",
      error: null,
    }));

    try {
      // Use POST and read SSE stream
      const response = await api.fetchAuth("/chat", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ projectId, content }),
      });

      if (!response.ok) {
        throw new Error(`Chat error: ${response.status}`);
      }

      const reader = response.body?.getReader();
      if (!reader) throw new Error("No response stream");

      const decoder = new TextDecoder();
      let fullContent = "";

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;

        const text = decoder.decode(value, { stream: true });
        const lines = text.split("\n");

        for (const line of lines) {
          if (line.startsWith("event: chunk")) {
            // Next line has data
            continue;
          }
          if (line.startsWith("data: ")) {
            try {
              const data = JSON.parse(line.slice(6));
              if (data.text) {
                fullContent += data.text;
                set({ streamingContent: fullContent });
              }
            } catch {
              // Skip malformed JSON
            }
          }
          if (line.startsWith("event: done")) {
            // Stream complete
          }
          if (line.startsWith("event: error")) {
            const nextLine = lines[lines.indexOf(line) + 1];
            if (nextLine?.startsWith("data: ")) {
              const err = JSON.parse(nextLine.slice(6));
              set({ error: err.message });
            }
          }
        }
      }

      // Add assistant message
      const assistantMsg: Message = {
        id: crypto.randomUUID(),
        projectId,
        role: "assistant",
        content: fullContent,
        createdAt: new Date().toISOString(),
      };

      set((s) => ({
        messages: [...s.messages, assistantMsg],
        streaming: false,
        streamingContent: "",
      }));
    } catch (err: any) {
      set({ streaming: false, streamingContent: "", error: err.message });
    }
  },

  clearMessages: () => set({ messages: [], streamingContent: "", error: null }),
}));
